/**
 * GatherVoices AI Copilot — shared UI primitives.
 *
 * Global rules (embedded in every UI surface here):
 *  - Every AI output is a DRAFT — never auto-sent, auto-published, or auto-saved.
 *  - Label AI-generated content subtly with "Drafted for you — edit anything".
 *  - Show privacy assurance ("Your words are never used to train AI models").
 */

import { useState } from "react";
import { toast } from "sonner";
import {
  Sparkles, Wand2, X, Loader2, RefreshCw, Check, ChevronLeft, ChevronRight, Info,
} from "lucide-react";
import api from "@/lib/api";

// -----------------------------------------------------------------------------
// Subtle draft label — used wherever an AI draft is shown.
// -----------------------------------------------------------------------------
export function AIDraftLabel({ className = "" }) {
  return (
    <div
      data-testid="ai-draft-label"
      className={`inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))] ${className}`}
    >
      <Sparkles size={12} className="text-[hsl(var(--accent))]" />
      Drafted for you — edit anything
    </div>
  );
}

// -----------------------------------------------------------------------------
// A little "Help me write this" pill button — inline beside text fields.
// -----------------------------------------------------------------------------
export function AIHelperButton({ label = "Help me write this", onClick, testid = "ai-helper-button" }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[hsl(var(--accent))] hover:opacity-80 transition-opacity"
    >
      <Wand2 size={14} />
      {label}
    </button>
  );
}

// -----------------------------------------------------------------------------
// Basic modal shell (framer-motion-free to keep bundle light).
// -----------------------------------------------------------------------------
function Modal({ open, onClose, children, testid = "ai-modal", widthClass = "max-w-lg" }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] grid place-items-center px-4" data-testid={testid}>
      <button
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
      />
      <div className={`relative ${widthClass} w-full max-h-[92vh] overflow-y-auto rounded-2xl bg-[hsl(var(--card))] border border-[hsl(var(--border))] shadow-2xl ever-fade-in`}>
        <button
          onClick={onClose}
          data-testid="ai-modal-close"
          className="absolute top-3 right-3 h-9 w-9 grid place-items-center rounded-lg hover:bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]"
        >
          <X size={18} />
        </button>
        {children}
      </div>
    </div>
  );
}

function PrivacyLine() {
  return (
    <p className="mt-3 flex items-start gap-1.5 text-[11px] text-[hsl(var(--muted-foreground))]">
      <Info size={12} className="mt-[2px] shrink-0" />
      Your words are only used to draft this text — never used to train AI models.
    </p>
  );
}

// =============================================================================
// 1. Story Coach — mini-interview → 3 tone drafts
// =============================================================================
const STORY_QUESTIONS = [
  { k: "relationship_note", q: "Who were they to you?", placeholder: "e.g. My grandmother, the one who raised me on Sunday afternoons" },
  { k: "three_words", q: "Three words that describe them", placeholder: "e.g. warm, curious, funny" },
  { k: "passion", q: "A passion or quirk everyone knew?", placeholder: "e.g. She baked pies for the whole street" },
  { k: "feeling", q: "What should people feel at this?", placeholder: "e.g. Grateful and gently uplifted" },
  { k: "moment", q: "One story worth remembering?", placeholder: "e.g. Reading me stories on the porch" },
];

export function AIStoryCoach({
  open,
  onClose,
  onApply,
  eventType,
  honoreeName,
  relationship,
  initialAnswers,
  testidPrefix = "ai-story",
}) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState(initialAnswers || {});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // {drafts, suggested_title, suggested_music_mood}
  const [pickedTone, setPickedTone] = useState(null);

  const total = STORY_QUESTIONS.length;
  const cur = STORY_QUESTIONS[step];

  const setAnswer = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));

  const canNext = () => (answers[cur?.k] || "").trim().length > 0 || step > 0;

  const generate = async (regen = false) => {
    setLoading(true);
    try {
      const res = await api.post("/ai/story-drafts", {
        event_type: eventType || "other",
        honoree_name: honoreeName || "",
        relationship: relationship || "",
        answers,
      });
      setResult(res.data);
      setPickedTone(res.data.drafts?.[0]?.tone || "heartfelt");
      if (regen) toast.success("New drafts ready");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not draft — please try again");
    } finally { setLoading(false); }
  };

  const restart = () => { setResult(null); setPickedTone(null); setStep(0); };

  const currentDraft = () => result?.drafts?.find((d) => d.tone === pickedTone) || result?.drafts?.[0];

  const applyDraft = () => {
    const d = currentDraft();
    if (!d) return;
    onApply?.({ story: d.text, suggested_title: result?.suggested_title, suggested_music_mood: result?.suggested_music_mood });
    onClose?.();
  };

  return (
    <Modal open={open} onClose={onClose} testid={`${testidPrefix}-modal`}>
      <div className="p-6 sm:p-7">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 grid place-items-center rounded-xl bg-[hsl(var(--accent-soft,35_55%_92%))]" style={{ background: "hsl(35 55% 92%)" }}>
            <Sparkles size={18} className="text-[hsl(var(--accent))]" />
          </div>
          <div>
            <h3 className="font-display text-lg font-semibold">Help me write this</h3>
            <p className="text-xs text-[hsl(var(--muted-foreground))]">A 60-second guided draft. Every word stays yours.</p>
          </div>
        </div>

        {!result ? (
          <div className="mt-6" data-testid={`${testidPrefix}-interview`}>
            <div className="flex items-center gap-2 text-xs font-semibold text-[hsl(var(--muted-foreground))]">
              <span>Question {step + 1} of {total}</span>
              <div className="flex-1 h-1.5 rounded-full bg-[hsl(var(--border))] overflow-hidden">
                <div className="h-full bg-[hsl(var(--accent))]" style={{ width: `${((step + 1) / total) * 100}%` }} />
              </div>
            </div>
            <label className="mt-4 block text-sm font-semibold">{cur.q}</label>
            <textarea
              data-testid={`${testidPrefix}-answer-${cur.k}`}
              value={answers[cur.k] || ""}
              onChange={(e) => setAnswer(cur.k, e.target.value)}
              rows={3}
              placeholder={cur.placeholder}
              className="mt-2 w-full rounded-xl p-3 bg-[hsl(var(--background))] border border-[hsl(var(--input))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))] resize-none"
            />
            <div className="mt-5 flex items-center gap-3">
              {step > 0 && (
                <button
                  data-testid={`${testidPrefix}-prev`}
                  onClick={() => setStep((s) => Math.max(0, s - 1))}
                  className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-semibold hover:bg-[hsl(var(--muted))]"
                >
                  <ChevronLeft size={16} /> Back
                </button>
              )}
              <div className="flex-1" />
              <button
                data-testid={`${testidPrefix}-skip-all`}
                onClick={() => generate(false)}
                disabled={loading}
                className="text-sm font-semibold text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
              >
                Skip &amp; draft now
              </button>
              {step < total - 1 ? (
                <button
                  data-testid={`${testidPrefix}-next`}
                  disabled={!canNext()}
                  onClick={() => setStep((s) => s + 1)}
                  className="inline-flex items-center gap-1 px-4 py-2.5 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-sm font-semibold disabled:opacity-50"
                >
                  Next <ChevronRight size={16} />
                </button>
              ) : (
                <button
                  data-testid={`${testidPrefix}-generate`}
                  onClick={() => generate(false)}
                  disabled={loading}
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-sm font-semibold disabled:opacity-60"
                >
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} Draft it
                </button>
              )}
            </div>
            <PrivacyLine />
          </div>
        ) : (
          <div className="mt-6" data-testid={`${testidPrefix}-results`}>
            <AIDraftLabel />
            <div className="mt-3 flex flex-wrap gap-2">
              {result.drafts.map((d) => (
                <button
                  key={d.tone}
                  data-testid={`${testidPrefix}-tone-${d.tone}`}
                  onClick={() => setPickedTone(d.tone)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                    pickedTone === d.tone
                      ? "bg-[hsl(var(--accent))] text-white border-[hsl(var(--accent))]"
                      : "bg-[hsl(var(--background))] border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]"
                  }`}
                >
                  {d.tone.charAt(0).toUpperCase() + d.tone.slice(1)}
                </button>
              ))}
            </div>
            <textarea
              data-testid={`${testidPrefix}-draft-text`}
              value={currentDraft()?.text || ""}
              onChange={(e) => {
                const text = e.target.value;
                setResult((r) => ({
                  ...r,
                  drafts: r.drafts.map((d) => (d.tone === pickedTone ? { ...d, text } : d)),
                }));
              }}
              rows={7}
              className="mt-3 w-full rounded-xl p-3.5 bg-[hsl(var(--background))] border border-[hsl(var(--input))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))] resize-none leading-relaxed"
            />
            {(result.suggested_title || result.suggested_music_mood) && (
              <div className="mt-3 rounded-xl bg-[hsl(var(--muted))] p-3 text-xs text-[hsl(var(--muted-foreground))]">
                {result.suggested_title && (
                  <div><span className="font-semibold">Suggested title:</span> {result.suggested_title}</div>
                )}
                {result.suggested_music_mood && (
                  <div className="mt-0.5"><span className="font-semibold">Music mood idea:</span> {result.suggested_music_mood}</div>
                )}
              </div>
            )}
            <div className="mt-5 flex items-center gap-2">
              <button
                data-testid={`${testidPrefix}-restart`}
                onClick={restart}
                className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-semibold hover:bg-[hsl(var(--muted))]"
              >
                <ChevronLeft size={16} /> Answer again
              </button>
              <button
                data-testid={`${testidPrefix}-regenerate`}
                onClick={() => generate(true)}
                disabled={loading}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] disabled:opacity-60"
              >
                {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Regenerate
              </button>
              <div className="flex-1" />
              <button
                data-testid={`${testidPrefix}-apply`}
                onClick={applyDraft}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-sm font-semibold"
              >
                <Check size={16} /> Use this draft
              </button>
            </div>
            <PrivacyLine />
          </div>
        )}
      </div>
    </Modal>
  );
}

// =============================================================================
// 2. Inline Rewrite — tone slider
// =============================================================================
const REWRITE_TONES = [
  { k: "warmer", label: "Warmer" },
  { k: "formal", label: "More formal" },
  { k: "shorter", label: "Shorter" },
  { k: "longer", label: "Longer" },
];

export function AIRewriteSheet({ open, onClose, currentText, onApply, context, testidPrefix = "ai-rewrite" }) {
  const [tone, setTone] = useState("warmer");
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState("");

  const run = async () => {
    if (!currentText?.trim()) { toast.error("Write a few words first"); return; }
    setLoading(true);
    try {
      const res = await api.post("/ai/rewrite", { text: currentText, tone, context });
      setDraft(res.data.text);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not rewrite");
    } finally { setLoading(false); }
  };

  return (
    <Modal open={open} onClose={onClose} testid={`${testidPrefix}-modal`}>
      <div className="p-6 sm:p-7">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 grid place-items-center rounded-xl" style={{ background: "hsl(35 55% 92%)" }}>
            <Wand2 size={18} className="text-[hsl(var(--accent))]" />
          </div>
          <div>
            <h3 className="font-display text-lg font-semibold">Rewrite this</h3>
            <p className="text-xs text-[hsl(var(--muted-foreground))]">Pick a direction. Your original stays until you apply.</p>
          </div>
        </div>

        <div className="mt-5">
          <div className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Your current text</div>
          <div className="mt-1.5 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--muted))] p-3 text-sm leading-relaxed max-h-40 overflow-y-auto">
            {currentText || <em>(empty)</em>}
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {REWRITE_TONES.map((t) => (
            <button
              key={t.k}
              data-testid={`${testidPrefix}-tone-${t.k}`}
              onClick={() => setTone(t.k)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                tone === t.k
                  ? "bg-[hsl(var(--accent))] text-white border-[hsl(var(--accent))]"
                  : "bg-[hsl(var(--background))] border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]"
              }`}
            >
              {t.label}
            </button>
          ))}
          <div className="flex-1" />
          <button
            data-testid={`${testidPrefix}-run`}
            onClick={run}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-sm font-semibold disabled:opacity-60"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Draft
          </button>
        </div>

        {draft && (
          <div className="mt-5">
            <AIDraftLabel />
            <textarea
              data-testid={`${testidPrefix}-draft-text`}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={6}
              className="mt-2 w-full rounded-xl p-3.5 bg-[hsl(var(--background))] border border-[hsl(var(--input))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))] resize-none leading-relaxed"
            />
            <div className="mt-4 flex items-center gap-2">
              <button
                data-testid={`${testidPrefix}-regenerate`}
                onClick={run}
                disabled={loading}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] disabled:opacity-60"
              >
                {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Regenerate
              </button>
              <div className="flex-1" />
              <button
                data-testid={`${testidPrefix}-apply`}
                onClick={() => { onApply?.(draft); onClose?.(); }}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-sm font-semibold"
              >
                <Check size={16} /> Use this
              </button>
            </div>
          </div>
        )}
        <PrivacyLine />
      </div>
    </Modal>
  );
}

// =============================================================================
// 3. Contributor Script Coach — used inside the Recorder before recording
// =============================================================================
const SCRIPT_QUESTIONS = [
  { k: "relationship", q: "How did you know them?", placeholder: "e.g. College roommate, coach, colleague…" },
  { k: "memory", q: "One memory that always comes back?", placeholder: "e.g. Late-night pizza the night before finals" },
  { k: "message", q: "What would you say to them / the family today?", placeholder: "e.g. Thank you for shaping who I am" },
];

const SCRIPT_TONES = [
  { k: "heartfelt", label: "Heartfelt" },
  { k: "funny", label: "Funny" },
  { k: "brief", label: "Brief" },
];

export function AIScriptCoach({
  open,
  onClose,
  slug,
  contributorName,
  onApply,
  testidPrefix = "ai-script",
}) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [tone, setTone] = useState("heartfelt");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // {script, estimated_seconds, tone}

  const total = SCRIPT_QUESTIONS.length;
  const cur = SCRIPT_QUESTIONS[step];

  const setAnswer = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));

  const generate = async () => {
    setLoading(true);
    try {
      const res = await api.post("/ai/script-draft", {
        slug,
        contributor_name: contributorName || "",
        tone,
        answers,
      });
      setResult(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not draft — please try again");
    } finally { setLoading(false); }
  };

  const restart = () => { setResult(null); setStep(0); };

  return (
    <Modal open={open} onClose={onClose} testid={`${testidPrefix}-modal`}>
      <div className="p-6 sm:p-7">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 grid place-items-center rounded-xl" style={{ background: "hsl(35 55% 92%)" }}>
            <Sparkles size={18} className="text-[hsl(var(--accent))]" />
          </div>
          <div>
            <h3 className="font-display text-lg font-semibold">Help me find the words</h3>
            <p className="text-xs text-[hsl(var(--muted-foreground))]">A short draft you can read on camera. Every word stays yours.</p>
          </div>
        </div>

        {!result ? (
          <div className="mt-6" data-testid={`${testidPrefix}-interview`}>
            <div className="flex items-center gap-2 text-xs font-semibold text-[hsl(var(--muted-foreground))]">
              <span>Question {step + 1} of {total}</span>
              <div className="flex-1 h-1.5 rounded-full bg-[hsl(var(--border))] overflow-hidden">
                <div className="h-full bg-[hsl(var(--accent))]" style={{ width: `${((step + 1) / total) * 100}%` }} />
              </div>
            </div>
            <label className="mt-4 block text-sm font-semibold">{cur.q}</label>
            <textarea
              data-testid={`${testidPrefix}-answer-${cur.k}`}
              value={answers[cur.k] || ""}
              onChange={(e) => setAnswer(cur.k, e.target.value)}
              rows={3}
              placeholder={cur.placeholder}
              className="mt-2 w-full rounded-xl p-3 bg-[hsl(var(--background))] border border-[hsl(var(--input))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))] resize-none"
            />

            {step === total - 1 && (
              <div className="mt-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Tone</div>
                <div className="mt-2 flex gap-2">
                  {SCRIPT_TONES.map((t) => (
                    <button
                      key={t.k}
                      data-testid={`${testidPrefix}-tone-${t.k}`}
                      onClick={() => setTone(t.k)}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${
                        tone === t.k
                          ? "bg-[hsl(var(--accent))] text-white border-[hsl(var(--accent))]"
                          : "bg-[hsl(var(--background))] border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]"
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-5 flex items-center gap-3">
              {step > 0 && (
                <button
                  onClick={() => setStep((s) => Math.max(0, s - 1))}
                  className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-semibold hover:bg-[hsl(var(--muted))]"
                >
                  <ChevronLeft size={16} /> Back
                </button>
              )}
              <div className="flex-1" />
              {step < total - 1 ? (
                <button
                  data-testid={`${testidPrefix}-next`}
                  onClick={() => setStep((s) => s + 1)}
                  className="inline-flex items-center gap-1 px-4 py-2.5 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-sm font-semibold"
                >
                  Next <ChevronRight size={16} />
                </button>
              ) : (
                <button
                  data-testid={`${testidPrefix}-generate`}
                  onClick={generate}
                  disabled={loading}
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-sm font-semibold disabled:opacity-60"
                >
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} Draft my script
                </button>
              )}
            </div>
            <PrivacyLine />
          </div>
        ) : (
          <div className="mt-6" data-testid={`${testidPrefix}-results`}>
            <div className="flex items-center justify-between">
              <AIDraftLabel />
              <span className="text-xs text-[hsl(var(--muted-foreground))]">≈ {result.estimated_seconds}s spoken</span>
            </div>
            <textarea
              data-testid={`${testidPrefix}-draft-text`}
              value={result.script}
              onChange={(e) => setResult((r) => ({ ...r, script: e.target.value }))}
              rows={9}
              className="mt-3 w-full rounded-xl p-3.5 bg-[hsl(var(--background))] border border-[hsl(var(--input))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))] resize-none leading-relaxed"
            />
            <div className="mt-4 flex items-center gap-2">
              <button
                data-testid={`${testidPrefix}-restart`}
                onClick={restart}
                className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-semibold hover:bg-[hsl(var(--muted))]"
              >
                <ChevronLeft size={16} /> Answer again
              </button>
              <button
                data-testid={`${testidPrefix}-regenerate`}
                onClick={generate}
                disabled={loading}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] disabled:opacity-60"
              >
                {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Regenerate
              </button>
              <div className="flex-1" />
              <button
                data-testid={`${testidPrefix}-apply`}
                onClick={() => { onApply?.(result.script); onClose?.(); }}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-sm font-semibold"
              >
                <Check size={16} /> I&apos;ll use this
              </button>
            </div>
            <PrivacyLine />
          </div>
        )}
      </div>
    </Modal>
  );
}

// =============================================================================
// 4. Prompts suggester — one-shot AI to replace the default 6 prompts
// =============================================================================
export function AIPromptsButton({ eventType, honoreeName, story, onApply, testid = "ai-prompts-button" }) {
  const [loading, setLoading] = useState(false);
  const [prompts, setPrompts] = useState(null);

  const run = async () => {
    setLoading(true);
    try {
      const res = await api.post("/ai/contributor-prompts", {
        event_type: eventType,
        honoree_name: honoreeName,
        story: story || "",
      });
      setPrompts(res.data.prompts || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not draft prompts");
    } finally { setLoading(false); }
  };

  return (
    <div className="rounded-xl border border-dashed border-[hsl(var(--border))] bg-[hsl(var(--muted))] p-4">
      <div className="flex items-center gap-2">
        <Sparkles size={16} className="text-[hsl(var(--accent))]" />
        <div className="text-sm font-semibold">Personalized contributor prompts</div>
      </div>
      <p className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">
        Draft 6 prompts unique to <strong>{honoreeName || "them"}</strong> based on the story you shared.
      </p>
      {!prompts ? (
        <button
          data-testid={testid}
          onClick={run}
          disabled={loading || !honoreeName || !story?.trim()}
          className="mt-3 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-xs font-semibold disabled:opacity-50"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Draft prompts
        </button>
      ) : (
        <div className="mt-3">
          <AIDraftLabel />
          <ol className="mt-2 space-y-1.5 list-decimal list-inside text-sm">
            {prompts.map((p, i) => (
              <li key={i} data-testid={`ai-prompt-${i}`}>{p}</li>
            ))}
          </ol>
          <div className="mt-3 flex items-center gap-2">
            <button
              onClick={run}
              disabled={loading}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold border border-[hsl(var(--border))] hover:bg-[hsl(var(--background))] disabled:opacity-60"
            >
              {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Try again
            </button>
            <button
              data-testid="ai-prompts-apply"
              onClick={() => onApply?.(prompts)}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-xs font-semibold"
            >
              <Check size={12} /> Use these
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// 5. Message Template Studio — chips → draft invite/reminder/thank-you copy
// =============================================================================
const AUDIENCES = [
  { k: "family", label: "Close family" },
  { k: "friends", label: "Friends" },
  { k: "colleagues", label: "Colleagues" },
  { k: "community", label: "Community" },
];
const MESSAGE_TONES = [
  { k: "warm", label: "Warm" },
  { k: "formal", label: "Formal" },
  { k: "light", label: "Light" },
];
const MESSAGE_TYPES = [
  { k: "invite", label: "Invitation" },
  { k: "reminder", label: "Reminder" },
  { k: "video_ready", label: "Film is ready" },
  { k: "thank_you", label: "Thank-you" },
];

export function AIMessageStudio({ open, onClose, eventId, onApply, testidPrefix = "ai-message" }) {
  const [messageType, setMessageType] = useState("invite");
  const [audience, setAudience] = useState("family");
  const [tone, setTone] = useState("warm");
  const [extra, setExtra] = useState("");
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState(null); // {subject, body, ...}

  const run = async () => {
    setLoading(true);
    try {
      const res = await api.post("/ai/message-template", {
        event_id: eventId,
        message_type: messageType,
        audience,
        tone,
        channel: "email",
        extra_context: extra,
      });
      setDraft(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not draft the message");
    } finally { setLoading(false); }
  };

  return (
    <Modal open={open} onClose={onClose} testid={`${testidPrefix}-modal`} widthClass="max-w-xl">
      <div className="p-6 sm:p-7">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 grid place-items-center rounded-xl" style={{ background: "hsl(35 55% 92%)" }}>
            <Wand2 size={18} className="text-[hsl(var(--accent))]" />
          </div>
          <div>
            <h3 className="font-display text-lg font-semibold">Message studio</h3>
            <p className="text-xs text-[hsl(var(--muted-foreground))]">Pick audience + tone, get a draft with merge fields. Nothing sends automatically.</p>
          </div>
        </div>

        <Chips label="Message" options={MESSAGE_TYPES} value={messageType} onChange={setMessageType} testid={`${testidPrefix}-type`} />
        <Chips label="Audience" options={AUDIENCES} value={audience} onChange={setAudience} testid={`${testidPrefix}-audience`} />
        <Chips label="Tone" options={MESSAGE_TONES} value={tone} onChange={setTone} testid={`${testidPrefix}-tone`} />

        <div className="mt-4">
          <label className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Anything specific to include? (optional)</label>
          <textarea
            data-testid={`${testidPrefix}-extra`}
            value={extra}
            onChange={(e) => setExtra(e.target.value)}
            rows={2}
            placeholder="e.g. Mention that the film will play at Saturday's gathering"
            className="mt-1.5 w-full rounded-xl p-3 bg-[hsl(var(--background))] border border-[hsl(var(--input))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))] resize-none text-sm"
          />
        </div>

        <div className="mt-4">
          <button
            data-testid={`${testidPrefix}-generate`}
            onClick={run}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-sm font-semibold disabled:opacity-60"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} {draft ? "Regenerate" : "Draft message"}
          </button>
        </div>

        {draft && (
          <div className="mt-5">
            <AIDraftLabel />
            {draft.subject && (
              <div className="mt-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Subject</div>
                <input
                  data-testid={`${testidPrefix}-subject`}
                  value={draft.subject}
                  onChange={(e) => setDraft((d) => ({ ...d, subject: e.target.value }))}
                  className="mt-1.5 w-full h-11 rounded-xl px-3 bg-[hsl(var(--background))] border border-[hsl(var(--input))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))] text-sm"
                />
              </div>
            )}
            <div className="mt-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Body</div>
              <textarea
                data-testid={`${testidPrefix}-body`}
                value={draft.body}
                onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))}
                rows={8}
                className="mt-1.5 w-full rounded-xl p-3.5 bg-[hsl(var(--background))] border border-[hsl(var(--input))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))] resize-none leading-relaxed text-sm"
              />
            </div>
            <p className="mt-2 text-[11px] text-[hsl(var(--muted-foreground))]">Merge fields left as {"{first_name}"}, {"{honoree}"}, {"{deadline}"}, {"{tribute_url}"} are filled in when sending.</p>
            <div className="mt-4 flex items-center gap-2">
              <div className="flex-1" />
              <button
                data-testid={`${testidPrefix}-apply`}
                onClick={() => { onApply?.(draft); onClose?.(); toast.success("Draft loaded into the note. Review before sending."); }}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-sm font-semibold"
              >
                <Check size={16} /> Load into personal note
              </button>
            </div>
          </div>
        )}
        <PrivacyLine />
      </div>
    </Modal>
  );
}

function Chips({ label, options, value, onChange, testid }) {
  return (
    <div className="mt-4">
      <div className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">{label}</div>
      <div className="mt-2 flex flex-wrap gap-2">
        {options.map((o) => (
          <button
            key={o.k}
            data-testid={`${testid}-${o.k}`}
            onClick={() => onChange(o.k)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
              value === o.k
                ? "bg-[hsl(var(--accent))] text-white border-[hsl(var(--accent))]"
                : "bg-[hsl(var(--background))] border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]"
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}
